import { Collapse, FormControlLabel, InputAdornment, Stack, Typography } from '@mui/material';
import { isNil } from 'lodash';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useDevices } from '@/api/devices';
import {
    usePostActivateWaveHeightAlert,
    usePostDeactivateWaveHeightAlert,
    usePostUpdateWaveHeightAlert,
} from '@/api/waveHeight';
import { waveHeightBulkIcon } from '@/assets';
import { SensorIcon } from '@/components/data/SensorIcon';
import { FormInputText } from '@/components/form/FormInputText';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Switch } from '@/components/ui/Switch';
import { ResetUpdateButtonGroup } from '@/components/userAccount/ResetUpdateButtonGroup';
import { useSpotterContext } from '@/context/SpotterContext';
import { useDisclosure } from '@/hooks/useDisclosure';

type WaveHeightAlertFormValues = {
    waveHeightInMeters: string;
};

const waveHeightDefaultValues = { waveHeightInMeters: '' };

export const WaveHeightAlertSection = ({ readOnly }: { readOnly?: boolean }) => {
    const { spotterId } = useSpotterContext();
    const { mutateAsync: activateDataWaveHeightAlert } = usePostActivateWaveHeightAlert();
    const { mutateAsync: deactivateDataWaveHeightAlert } = usePostDeactivateWaveHeightAlert();
    const { mutateAsync: updateDataWaveHeightAlert } = usePostUpdateWaveHeightAlert();
    const { data: devices, isFetching: isDevicesFetching } = useDevices({
        devices: [{ spotterId: spotterId as string }],
        config: { enabled: !!spotterId },
    });
    const [showLegacyWaveHeightAlert, setShowLegacyWaveHeightAlert] = useState(false);

    const {
        control: waveHeightAlertFormControl,
        reset: waveHeightAlertFormReset,
        handleSubmit: changeWaveHeightAlertHandleSubmit,
        formState: waveHeightAlertFormState,
    } = useForm<WaveHeightAlertFormValues>({
        defaultValues: waveHeightDefaultValues,
        mode: 'onChange',
    });
    const {
        isOpen: isWaveHeightAlertSwitchOn,
        toggle: toggleWaveHeightAlertSwitch,
        open: switchWaveHeightAlertOn,
        close: switchWaveHeightAlertOff,
    } = useDisclosure(devices?.[0].waves.waveHeightAlertEnabled);

    const { waveHeightAlertEnabled, waveHeightAlertHeight } = devices?.[0].waves ?? {};
    useEffect(() => {
        if (isDevicesFetching) return;

        if (showLegacyWaveHeightAlert) {
            // don't hide it if it's already been shown on this page load
            return;
        }
        setShowLegacyWaveHeightAlert(!!waveHeightAlertEnabled && !isNil(waveHeightAlertHeight));
    }, [isDevicesFetching, waveHeightAlertEnabled, waveHeightAlertHeight, showLegacyWaveHeightAlert]);

    const handleSwitchClick = async () => {
        if (!spotterId) {
            // eslint-disable-next-line no-console
            console.log('No Spotter selected');
            return;
        }
        if (isWaveHeightAlertSwitchOn) {
            await deactivateDataWaveHeightAlert({ spotterId: spotterId });
            toggleWaveHeightAlertSwitch();
            return;
        }
        await activateDataWaveHeightAlert({ spotterId: spotterId });
        toggleWaveHeightAlertSwitch();
    };

    const onWaveHeightReset = () => {
        waveHeightAlertFormReset({ waveHeightInMeters: `${devices?.[0].waves.waveHeightAlertHeight}` });
    };

    const onWaveHeightSubmit = async (formData: WaveHeightAlertFormValues) => {
        if (!spotterId) {
            console.warn('WaveHeightAlertSection: onWaveHeightSubmit: No Spotter selected');
            return;
        }
        try {
            await updateDataWaveHeightAlert({ height: Number(formData.waveHeightInMeters), spotterId: spotterId });
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        if (!devices) {
            return;
        }
        const isWaveHeightAlertOn = devices[0].waves.waveHeightAlertEnabled;
        if (isWaveHeightAlertSwitchOn === isWaveHeightAlertOn) {
            return;
        }

        if (isWaveHeightAlertOn) {
            switchWaveHeightAlertOn();
        } else {
            switchWaveHeightAlertOff();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [devices]);

    useEffect(() => {
        if (!devices) {
            return;
        }
        waveHeightAlertFormReset({ waveHeightInMeters: `${devices?.[0].waves.waveHeightAlertHeight}` });
    }, [devices, waveHeightAlertFormReset]);

    if (!showLegacyWaveHeightAlert) {
        // If you're not already using legacy wave heights, it's not an option. Use v2.
        return false;
    }

    return (
        <Stack gap={2}>
            <SectionHeader title="Wave height" icon={<SensorIcon src={waveHeightBulkIcon} />} />
            <FormControlLabel
                value="start"
                sx={{
                    ml: 0,
                    width: '100%',
                    justifyContent: 'space-between',
                    gap: 5,
                }}
                control={<Switch onClick={handleSwitchClick} checked={isWaveHeightAlertSwitchOn} readOnly={readOnly} />}
                label={
                    <Stack>
                        <Typography>Enable wave height notifications</Typography>
                        <Typography variant="minorLabel">
                            Receive an email alert when the significant wave height exceeds a preset value
                        </Typography>
                    </Stack>
                }
                labelPlacement="start"
            />
            <Collapse in={isWaveHeightAlertSwitchOn}>
                <Stack direction="row" gap={2}>
                    <FormInputText
                        name="waveHeightInMeters"
                        control={waveHeightAlertFormControl}
                        label="Wave height (meters)"
                        inputFieldProps={{
                            id: 'waveHeightAlert',
                            type: 'number',
                            size: 'medium',
                            disabled: readOnly,
                            InputProps: {
                                endAdornment: <InputAdornment position="end">meters</InputAdornment>,
                            },
                        }}
                        controllerProps={{
                            rules: {
                                required: 'Field is required',
                                max: { value: 100, message: 'Max wave height threshold is 100 meters.' },
                                min: {
                                    value: 1,
                                    message: 'Min wave height threshold is 1 meter.',
                                },
                            },
                        }}
                    />
                    <ResetUpdateButtonGroup
                        onReset={onWaveHeightReset}
                        onSubmit={changeWaveHeightAlertHandleSubmit(onWaveHeightSubmit)}
                        isResetDisabled={!waveHeightAlertFormState.isDirty || waveHeightAlertFormState.isSubmitting}
                        isUpdateDisabled={!waveHeightAlertFormState.isDirty || waveHeightAlertFormState.isSubmitting}
                        isSubmitting={waveHeightAlertFormState.isSubmitting}
                        containerStyle={{
                            m: 0,
                            justifyContent: { xs: 'flex-end', sm: 'flex-start' },
                        }}
                    />
                </Stack>
            </Collapse>
        </Stack>
    );
};
